<template>
  <div class="ledger-page">
    <b-card
      v-if="loading"
      class="rounded-lg text-center py-3"
    >
      <b-spinner />
    </b-card>
    <b-card
      v-else-if="run"
      class="rounded-lg"
    >
      <h4>{{ run.year }}-{{ String(run.month).padStart(2, '0') }}</h4>
      <b-badge :variant="enumValue(run.status) === 'posted' ? 'success' : 'dark'">
        {{ enumLabel(run.status, $i18n.locale) }}
      </b-badge>
      <div class="ledger-num mt-1">
        {{ $t('ledger_rec_revenue') }} {{ money(run.total_revenue) }}
      </div>
      <div class="ledger-num">
        {{ $t('ledger_rec_wip') }} {{ money(run.total_wip_transferred) }}
      </div>
      <div class="mt-1">
        <b-link
          v-if="run.journal_entry_id && canJournal"
          class="mr-1"
          :to="{ name: 'ledger-journals-view', params: { id: run.journal_entry_id } }"
        >
          {{ $t('ledger_journal_open') }}
        </b-link>
        <b-link
          v-if="run.reversal_journal_entry_id && canJournal"
          :to="{ name: 'ledger-journals-view', params: { id: run.reversal_journal_entry_id } }"
        >
          {{ $t('ledger_depr_reversal_journal') }}
        </b-link>
      </div>
      <b-table
        class="mt-1 ledger-dense-table"
        :items="run.entries || []"
        :fields="fields"
        small
        responsive
      >
        <template #cell(percent_complete)="data">
          {{ percentLabel(data.item.percent_complete) }}
        </template>
        <template #cell(revenue_this_period)="data">
          <span :class="{ 'text-danger': Number(data.item.revenue_this_period) < 0 }">
            {{ money(data.item.revenue_this_period) }}
          </span>
        </template>
      </b-table>
      <b-button
        v-if="canRun && enumValue(run.status) === 'posted'"
        variant="outline-danger"
        class="rounded-pill"
        @click="confirmReverse"
      >
        {{ $t('ledger_rec_reverse') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BBadge, BLink, BTable, BButton, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getRecognitionRun, reverseRecognitionRun } from '@/api/accounting/contractRecognition'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'ContractRecognitionShow',
  components: {
    BCard, BBadge, BLink, BTable, BButton, BSpinner,
  },
  data() {
    return { loading: false, run: null }
  },
  computed: {
    canRun() {
      return this.hasPermission('run contract_recognition')
    },
    canJournal() {
      return this.hasPermission('view journal_entries')
    },
    fields() {
      return [
        { key: 'contract_number', label: this.$t('ledger_ct_number') },
        { key: 'percent_complete', label: this.$t('ledger_ct_percent') },
        { key: 'revenue_this_period', label: this.$t('ledger_rec_revenue') },
        { key: 'wip_transferred', label: this.$t('ledger_rec_wip') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumValue,
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    percentLabel(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AlertCircleIcon', variant },
      })
    },
    async load() {
      this.loading = true
      try {
        const res = await getRecognitionRun(this.$route.params.id)
        this.run = oneFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async confirmReverse() {
      const result = await Swal.fire({
        text: this.$t('ledger_rec_reverse_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_rec_reverse'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await reverseRecognitionRun(this.run.id)
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
  },
}
</script>
